/**
 * Cache persistente per i dati degli operatori usando IndexedDB
 * Salva i dati localmente per evitare di ri-scaricarli ad ogni visita
 */
import { ensureOperatorsSuccess } from "./operatorsApi";
import { supabase } from "./supabase";

const DB_NAME = 'dpm-operators-cache';
const DB_VERSION = 1;
const STORE_NAME = 'operators';
const CACHE_DURATION_HOURS = 24; // Cache valida per 24 ore

interface CacheEntry<T> {
  key: string;
  data: T;
  timestamp: number;
  expiresAt: number;
  totalCount?: number; // Count totale degli operatori nel database al momento del salvataggio
}

/**
 * Apre la connessione a IndexedDB
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.warn('[OperatorsCache] IndexedDB non disponibile');
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Crea object store se non esiste
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Genera una chiave cache basata sui filtri
 */
export function generateCacheKey(filters: {
  categories?: string[] | null;
  types?: string[] | null;
  region?: string | null;
  dateStart?: string | null;
  dateEnd?: string | null;
}): string {
  const normalize = (values?: string[] | null) =>
    (values || []).slice().sort().join("|") || "all";
  const parts = [
    'operators',
    normalize(filters.categories),
    normalize(filters.types),
    filters.region || 'all',
    filters.dateStart || 'none',
    filters.dateEnd || 'none',
  ];
  return parts.join(':');
}

/**
 * Salva dati in cache
 * @param key - Chiave cache
 * @param data - Dati da salvare
 * @param totalCount - Count totale degli operatori nel database (opzionale, per invalidazione automatica)
 */
export async function setCacheData<T>(key: string, data: T, totalCount?: number): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const entry: CacheEntry<T> = {
      key,
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + (CACHE_DURATION_HOURS * 60 * 60 * 1000),
      totalCount,
    };

    return new Promise((resolve, reject) => {
      const request = store.put(entry);

      request.onsuccess = () => {
        console.log(`[OperatorsCache] Salvati ${Array.isArray(data) ? data.length : 1} items per key: ${key}`);
        resolve();
      };

      request.onerror = () => {
        console.warn('[OperatorsCache] Errore salvataggio:', request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.warn('[OperatorsCache] Errore salvataggio:', error);
  }
}

/**
 * Recupera dati dalla cache
 * Ritorna null se non presenti o scaduti
 * @param key - Chiave cache
 * @param currentTotalCount - Count totale attuale degli operatori nel database (opzionale, per invalidazione automatica)
 */
export async function getCacheData<T>(key: string, currentTotalCount?: number): Promise<T | null> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve) => {
      const request = store.get(key);

      request.onsuccess = () => {
        const entry = request.result as CacheEntry<T> | undefined;

        if (!entry) {
          console.log(`[OperatorsCache] Cache miss per key: ${key}`);
          resolve(null);
          return;
        }

        // Verifica scadenza
        if (Date.now() > entry.expiresAt) {
          console.log(`[OperatorsCache] Cache scaduta per key: ${key}`);
          // Rimuovi entry scaduta in background
          deleteCacheData(key).catch(() => { });
          resolve(null);
          return;
        }

        // Verifica invalidazione basata sul count totale
        // Se la cache vecchia non ha totalCount, la invalidiamo sempre (cache creata prima dell'implementazione)
        if (entry.totalCount === undefined) {
          console.log(`[OperatorsCache] ⚠️ Cache invalidata: entry vecchia senza totalCount per key: ${key}`);
          deleteCacheData(key).catch(() => { });
          resolve(null);
          return;
        }

        // Se abbiamo un count corrente, confrontiamolo
        if (currentTotalCount !== undefined) {
          console.log(`[OperatorsCache] Validazione count: cache.totalCount=${entry.totalCount}, db.count=${currentTotalCount}`);

          if (currentTotalCount !== entry.totalCount) {
            console.log(`[OperatorsCache] ⚠️ Cache invalidata: count cambiato da ${entry.totalCount} a ${currentTotalCount} per key: ${key}`);
            // Rimuovi entry invalidata in background
            deleteCacheData(key).catch(() => { });
            resolve(null);
            return;
          }
          console.log(`[OperatorsCache] ✅ Count validato: ${entry.totalCount} === ${currentTotalCount}`);
        }

        const ageMinutes = Math.round((Date.now() - entry.timestamp) / (60 * 1000));
        console.log(`[OperatorsCache] Cache hit per key: ${key} (età: ${ageMinutes}m, totalCount: ${entry.totalCount || 'N/A'})`);
        resolve(entry.data);
      };

      request.onerror = () => {
        console.warn('[OperatorsCache] Errore lettura:', request.error);
        resolve(null);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.warn('[OperatorsCache] IndexedDB non disponibile:', error);
    return null;
  }
}

/**
 * Rimuove una entry dalla cache
 */
export async function deleteCacheData(key: string): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve) => {
      const request = store.delete(key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        resolve();
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.warn('[OperatorsCache] Errore rimozione:', error);
  }
}

/**
 * Svuota tutta la cache degli operatori
 */
export async function clearAllCache(): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve) => {
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[OperatorsCache] Cache svuotata');
        resolve();
      };

      request.onerror = () => {
        resolve();
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.warn('[OperatorsCache] Errore svuotamento:', error);
  }
}

/**
 * Ottiene il count totale degli operatori dal database
 * Utile per invalidare la cache quando i dati cambiano
 */
export async function getTotalOperatorsCount(token: string): Promise<number | null> {
  try {
    console.log('[OperatorsCache] Richiesta count totale operatori...');
    const { data, error } = await supabase.functions.invoke('operators-query', {
      body: { action: 'count', params: {} },
      headers: {
        'X-Portale-Token': token,
      },
    });

    const payload = ensureOperatorsSuccess<{ count: number }>(data, error);
    const count = payload?.count ?? null;
    console.log(`[OperatorsCache] Count totale ottenuto: ${count}`);
    return count;
  } catch (error) {
    console.warn('[OperatorsCache] Errore ottenimento count:', error);
    return null;
  }
}

/**
 * Ottiene statistiche sulla cache
 */
export async function getCacheStats(): Promise<{
  totalEntries: number;
  totalOperators: number;
  oldestEntry: Date | null;
}> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve) => {
      const request = store.getAll();

      request.onsuccess = () => {
        const entries = request.result as CacheEntry<unknown[]>[];

        let totalOperators = 0;
        let oldestTimestamp: number | null = null;

        for (const entry of entries) {
          if (Array.isArray(entry.data)) {
            totalOperators += entry.data.length;
          }
          if (oldestTimestamp === null || entry.timestamp < oldestTimestamp) {
            oldestTimestamp = entry.timestamp;
          }
        }

        resolve({
          totalEntries: entries.length,
          totalOperators,
          oldestEntry: oldestTimestamp ? new Date(oldestTimestamp) : null,
        });
      };

      request.onerror = () => {
        resolve({
          totalEntries: 0,
          totalOperators: 0,
          oldestEntry: null,
        });
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    return {
      totalEntries: 0,
      totalOperators: 0,
      oldestEntry: null,
    };
  }
}

